import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  Button,
} from "@mui/material";
import {
  Search,
  Refresh,
  Person,
  Visibility,
  LocationOn,
  MoreVert,
  Download,
} from "@mui/icons-material";
import { downloadUsersExcel, getAllUsers } from "../../services/Api";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";

const GetAllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);

  // Query state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedUser, setMenuSelectedUser] = useState(null);

  const navigate = useNavigate();

  const fetchUsers = async (currentPage, currentRowsPerPage, currentSearch) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllUsers(
        currentPage + 1,
        currentRowsPerPage,
        currentSearch,
      );
      if (response.success) {
        setUsers(response.data || []);
        setTotalUsers(
          response.pagination?.totalItems ||
            response.pagination?.total ||
            response.totalItems ||
            response.totalRecords ||
            response.total ||
            response.totalCount ||
            response.data?.length ||
            0,
        );
      } else {
        setError(response?.message || "Failed to fetch users");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "An error occurred while fetching users",
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((p, r, s) => fetchUsers(p, r, s), 1000),
    [],
  );

  useEffect(() => {
    debouncedFetch(page, rowsPerPage, search);
    return () => debouncedFetch.cancel();
  }, [page, rowsPerPage, search, debouncedFetch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchUsers(page, rowsPerPage, search);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedUser(null);
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await downloadUsersExcel();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Failed to download Excel file", error);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Users / Management
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 300 }}>
            User Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadExcel}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                backgroundColor: "#9C27B0",
                color: "white",
                borderColor: "#9C27B0",
                px: 3,
              }}
            >
              Download
            </Button>
            <TextField
              placeholder="Search users..."
              size="small"
              value={search}
              onChange={handleSearchChange}
              sx={{
                width: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  backgroundColor: "white",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 800 }} aria-label="users table">
            <TableHead sx={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mobile Number</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Joined Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          src={user.profileImage}
                          sx={{
                            width: 40,
                            height: 40,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "primary.light",
                          }}
                        >
                          {!user.profileImage && <Person />}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {user.name ||
                              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                              "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.phoneNumber || user.mobile || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={1}
        PaperProps={{
          sx: {
            minWidth: 160,
            border: "1px solid",
            borderColor: "text.border",
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/users/${menuSelectedUser?.id}/details`);
            handleMenuClose();
          }}
        >
          <MuiListItemIcon>
            <Visibility fontSize="small" color="primary" />
          </MuiListItemIcon>
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/users/${menuSelectedUser?.id}/address`);
            handleMenuClose();
          }}
        >
          <MuiListItemIcon>
            <LocationOn fontSize="small" color="secondary" />
          </MuiListItemIcon>
          View Address
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GetAllUsers;
